// frontend/src/components/Dashboard/CampaignEditor/sections/Maps.js
// DROP-IN replacement for your current Maps.js
//
// Fixes:
// - Grid showing only a single column after switching to CSS grid vars
//   by correctly setting --map-cols/--map-rows/--map-cell-size on the .map-grid element
//   and updating whenever mapWidth/mapHeight/zoom changes.
//
// Keeps:
// - Zoom controls (Ctrl/Cmd + wheel + buttons)
// - Right-mouse drag panning
// - Apply Resize button
// - User-managed categories + drag/drop tiles into categories
// - Upload zone always visible; uploads go to Custom Tiles

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { mapsAPI } from '../../../../services/api';
import './Maps.css';

// Built-in tiles
const TILE_TYPES = {
  empty: { name: 'Empty', image: null, category: 'basic' },
  floor: { name: 'Floor', image: '/assets/tiles/floor.png', category: 'basic' },
  wall: { name: 'Wall', image: '/assets/tiles/wall.png', category: 'basic' },
  door: { name: 'Door', image: '/assets/tiles/door.png', category: 'basic' },

  water: { name: 'Water', image: '/assets/tiles/water.png', category: 'terrain' },
  grass: { name: 'Grass', image: '/assets/tiles/grass.png', category: 'terrain' },
  tree: { name: 'Tree', image: '/assets/tiles/tree.png', category: 'terrain' },
  rock: { name: 'Rock', image: '/assets/tiles/rock.png', category: 'terrain' },
  rubble: { name: 'Rubble', image: '/assets/tiles/rubble.png', category: 'terrain' },

  chest: { name: 'Chest', image: '/assets/tiles/chest.png', category: 'objects' },
  table: { name: 'Table', image: '/assets/tiles/table.png', category: 'objects' },
  bed: { name: 'Bed', image: '/assets/tiles/bed.png', category: 'objects' },
  fire: { name: 'Fire', image: '/assets/tiles/fire.png', category: 'objects' },

  trap: { name: 'Trap', image: '/assets/tiles/trap.png', category: 'hazards' },
  poison: { name: 'Poison', image: '/assets/tiles/poison.png', category: 'hazards' },
  radiation: { name: 'Radiation', image: '/assets/tiles/radiation.png', category: 'hazards' },

  spawn: { name: 'Spawn Point', image: '/assets/tiles/spawn.png', category: 'markers' },
  exit: { name: 'Exit', image: '/assets/tiles/exit.png', category: 'markers' },
  objective: { name: 'Objective', image: '/assets/tiles/objective.png', category: 'markers' }
};

const CAT = {
  BASIC_ROOT: 'basic_root',
  CUSTOM_ROOT: 'custom_root',
  USER_ROOT: 'user_root',
  TERRAIN: 'basic_terrain',
  OBJECTS: 'basic_objects',
  HAZARDS: 'basic_hazards',
  MARKERS: 'basic_markers'
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function Maps({ campaign }) {
  const [maps, setMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map editor state
  const [mapName, setMapName] = useState('');
  const [mapWidth, setMapWidth] = useState(20);
  const [mapHeight, setMapHeight] = useState(20);
  const [gridData, setGridData] = useState([]);

  // Resize inputs (apply via button)
  const [pendingWidth, setPendingWidth] = useState(20);
  const [pendingHeight, setPendingHeight] = useState(20);

  const [selectedTile, setSelectedTile] = useState('floor');
  const [showGrid, setShowGrid] = useState(true);
  const [isPainting, setIsPainting] = useState(false);

  // Custom tiles
  const [customTiles, setCustomTiles] = useState([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef(null);

  // ----------------------------
  // Right-mouse drag panning
  // ----------------------------
  const canvasRef = useRef(null); // scroll container
  const gridRef = useRef(null);   // .map-grid element (CSS vars applied here)
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const [isPanning, setIsPanning] = useState(false);

  // ----------------------------
  // Zoom
  // ----------------------------
  const ZOOM_MIN = 0.4;
  const ZOOM_MAX = 3.0;
  const ZOOM_STEP = 0.1;
  const BASE_CELL_SIZE = 48;
  const [zoom, setZoom] = useState(1);

  const cellSizePx = Math.round(BASE_CELL_SIZE * zoom);

  // ✅ APPLY CSS VARS DIRECTLY TO THE GRID ELEMENT
  useEffect(() => {
    const g = gridRef.current;
    if (!g) return;
    g.style.setProperty('--map-cell-size', `${cellSizePx}px`);
  }, [cellSizePx]);

  const zoomAtPoint = useCallback((nextZoom, clientX, clientY) => {
    const el = canvasRef.current;
    if (!el) return;

    const prevZoom = zoom;
    const safeNextZoom = clamp(nextZoom, ZOOM_MIN, ZOOM_MAX);
    if (safeNextZoom === prevZoom) return;

    const rect = el.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const offsetY = clientY - rect.top;

    const ratio = safeNextZoom / prevZoom;

    const newScrollLeft = (el.scrollLeft + offsetX) * ratio - offsetX;
    const newScrollTop = (el.scrollTop + offsetY) * ratio - offsetY;

    setZoom(safeNextZoom);

    requestAnimationFrame(() => {
      el.scrollLeft = newScrollLeft;
      el.scrollTop = newScrollTop;
    });
  }, [zoom]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();

      const direction = e.deltaY > 0 ? -1 : 1;
      const factor = 1 + direction * 0.08;
      zoomAtPoint(zoom * factor, e.clientX, e.clientY);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoom, zoomAtPoint]);

  const zoomIn = () => {
    const el = canvasRef.current;
    if (!el) return setZoom((z) => clamp(Number((z + ZOOM_STEP).toFixed(2)), ZOOM_MIN, ZOOM_MAX));
    const rect = el.getBoundingClientRect();
    zoomAtPoint(zoom + ZOOM_STEP, rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  const zoomOut = () => {
    const el = canvasRef.current;
    if (!el) return setZoom((z) => clamp(Number((z - ZOOM_STEP).toFixed(2)), ZOOM_MIN, ZOOM_MAX));
    const rect = el.getBoundingClientRect();
    zoomAtPoint(zoom - ZOOM_STEP, rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  const zoomReset = () => {
    const el = canvasRef.current;
    if (!el) return setZoom(1);
    const rect = el.getBoundingClientRect();
    zoomAtPoint(1, rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  // ----------------------------
  // Category tree + drag/drop
  // ----------------------------
  const [categories, setCategories] = useState([
    { id: CAT.BASIC_ROOT, name: 'Basic', parentId: null, isSystem: true },
    { id: CAT.TERRAIN, name: 'Terrain', parentId: CAT.BASIC_ROOT, isSystem: true },
    { id: CAT.OBJECTS, name: 'Objects', parentId: CAT.BASIC_ROOT, isSystem: true },
    { id: CAT.HAZARDS, name: 'Hazards', parentId: CAT.BASIC_ROOT, isSystem: true },
    { id: CAT.MARKERS, name: 'Markers', parentId: CAT.BASIC_ROOT, isSystem: true },

    { id: CAT.CUSTOM_ROOT, name: 'Custom Tiles', parentId: null, isSystem: true },
    { id: CAT.USER_ROOT, name: 'My Categories', parentId: null, isSystem: true }
  ]);

  const [expanded, setExpanded] = useState(() => ({
    [CAT.BASIC_ROOT]: true,
    [CAT.TERRAIN]: true,
    [CAT.OBJECTS]: false,
    [CAT.HAZARDS]: false,
    [CAT.MARKERS]: false,
    [CAT.CUSTOM_ROOT]: true,
    [CAT.USER_ROOT]: true
  }));

  const [tileCategory, setTileCategory] = useState(() => {
    const init = {};
    Object.entries(TILE_TYPES).forEach(([key, t]) => {
      if (t.category === 'basic') init[key] = CAT.BASIC_ROOT;
      else if (t.category === 'terrain') init[key] = CAT.TERRAIN;
      else if (t.category === 'objects') init[key] = CAT.OBJECTS;
      else if (t.category === 'hazards') init[key] = CAT.HAZARDS;
      else if (t.category === 'markers') init[key] = CAT.MARKERS;
      else init[key] = CAT.BASIC_ROOT;
    });
    return init;
  });

  const [newCatName, setNewCatName] = useState('');
  const [newCatParent, setNewCatParent] = useState(CAT.USER_ROOT);

  const toggleExpanded = (catId) => setExpanded((prev) => ({ ...prev, [catId]: !prev[catId] }));
  const makeCatId = () => `usercat_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  const addCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    const id = makeCatId();
    const parentId = newCatParent || null;
    setCategories((prev) => [...prev, { id, name, parentId, isSystem: false }]);
    setExpanded((prev) => ({ ...prev, [parentId]: true, [id]: true }));
    setNewCatName('');
    setError(null);
  };

  const getCategoryById = (id) => categories.find((c) => c.id === id);

  const childrenByParent = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => {
      const pid = c.parentId ?? '__root__';
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(c);
    });

    for (const [, arr] of map.entries()) {
      arr.sort((a, b) => {
        if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
    }
    return map;
  }, [categories]);

  const getChildren = (parentId) => childrenByParent.get(parentId ?? '__root__') || [];

  const allTileTypes = useMemo(() => {
    const all = { ...TILE_TYPES };
    customTiles.forEach((t) => (all[t.key] = t));
    return all;
  }, [customTiles]);

  const tilesInCategory = useMemo(() => {
    const buckets = new Map();
    Object.keys(allTileTypes).forEach((tileKey) => {
      const catId = tileCategory[tileKey] || CAT.BASIC_ROOT;
      if (!buckets.has(catId)) buckets.set(catId, []);
      buckets.get(catId).push({ tileKey, tile: allTileTypes[tileKey] });
    });

    for (const [catId, arr] of buckets.entries()) {
      arr.sort((a, b) => {
        if (a.tileKey === 'empty') return -1;
        if (b.tileKey === 'empty') return 1;
        return (a.tile?.name || a.tileKey).localeCompare(b.tile?.name || b.tileKey);
      });
      buckets.set(catId, arr);
    }

    return buckets;
  }, [allTileTypes, tileCategory]);

  const startTileDrag = (e, tileKey) => {
    e.dataTransfer.setData('text/tileKey', tileKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onCategoryDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onCategoryDrop = (e, catId) => {
    e.preventDefault();
    const tileKey = e.dataTransfer.getData('text/tileKey');
    if (!tileKey) return;
    if (!getCategoryById(catId)) return;

    setTileCategory((prev) => ({ ...prev, [tileKey]: catId }));
    setExpanded((prev) => ({ ...prev, [catId]: true }));
  };

  // ----------------------------
  // Load maps
  // ----------------------------
  useEffect(() => {
    loadMaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id]);

  useEffect(() => {
    if (isCreating || isEditing) {
      if (!gridData || gridData.length === 0) {
        setGridData(resizeGrid([], mapWidth, mapHeight));
      }
      setPendingWidth(mapWidth);
      setPendingHeight(mapHeight);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreating, isEditing]);

  useEffect(() => {
    const onWindowMouseMove = (e) => {
      if (!isPanningRef.current) return;
      const el = canvasRef.current;
      if (!el) return;

      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;

      el.scrollLeft = panStartRef.current.scrollLeft - dx;
      el.scrollTop = panStartRef.current.scrollTop - dy;
    };

    const stopPanning = () => {
      if (!isPanningRef.current) return;
      isPanningRef.current = false;
      setIsPanning(false);
    };

    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', stopPanning);
    window.addEventListener('blur', stopPanning);

    return () => {
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', stopPanning);
      window.removeEventListener('blur', stopPanning);
    };
  }, []);

  const loadMaps = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await mapsAPI.getCampaignMaps(campaign.id);
      setMaps(res.data?.maps || []);
    } catch (e) {
      console.error('Failed to load maps:', e);
      setError(e?.response?.data?.error || 'Failed to load maps');
    } finally {
      setIsLoading(false);
    }
  };

  const resizeGrid = (prevGrid, newW, newH) => {
    return Array.from({ length: newH }, (_, y) =>
      Array.from({ length: newW }, (_, x) => prevGrid?.[y]?.[x] ?? 'empty')
    );
  };

  const initializeGrid = (width, height) => {
    const newGrid = Array.from({ length: height }, () => Array.from({ length: width }, () => 'empty'));
    setGridData(newGrid);
  };

  // ----------------------------
  // Map CRUD
  // ----------------------------
  const handleCreateNew = () => {
    setMapName('');
    setMapWidth(20);
    setMapHeight(20);
    setPendingWidth(20);
    setPendingHeight(20);
    setGridData([]);
    setSelectedTile('floor');
    setIsCreating(true);
    setIsEditing(false);
    setSelectedMap(null);
  };

  const handleEditMap = (map) => {
    setSelectedMap(map);
    setMapName(map.name);
    setMapWidth(map.width);
    setMapHeight(map.height);
    setPendingWidth(map.width);
    setPendingHeight(map.height);
    setGridData(JSON.parse(map.gridData));
    setSelectedTile('floor');
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleSaveMap = async () => {
    if (!mapName.trim()) return setError('Please enter a map name');
    if (mapWidth < 5 || mapHeight < 5) return setError('Map must be at least 5x5 squares (25ft x 25ft)');
    if (mapWidth > 100 || mapHeight > 100) return setError('Map cannot exceed 100x100 squares (500ft x 500ft)');

    try {
      setIsSaving(true);
      setError(null);

      const mapData = {
        name: mapName,
        width: mapWidth,
        height: mapHeight,
        gridData: JSON.stringify(gridData)
      };

      if (isEditing && selectedMap) {
        await mapsAPI.updateCampaignMap(campaign.id, selectedMap.id, mapData);
      } else {
        await mapsAPI.createCampaignMap(campaign.id, mapData);
      }

      await loadMaps();
      setIsCreating(false);
      setIsEditing(false);
      setSelectedMap(null);
      setGridData([]);
    } catch (e) {
      console.error('Failed to save map:', e);
      setError(e?.response?.data?.error || 'Failed to save map');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMap = async (mapId) => {
    if (!window.confirm('Are you sure you want to delete this map?')) return;
    try {
      await mapsAPI.deleteCampaignMap(campaign.id, mapId);
      await loadMaps();
      if (selectedMap?.id === mapId) {
        setSelectedMap(null);
        setIsEditing(false);
      }
    } catch (e) {
      console.error('Failed to delete map:', e);
      setError(e?.response?.data?.error || 'Failed to delete map');
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    setSelectedMap(null);
    setGridData([]);
    setError(null);
  };

  // ----------------------------
  // Resize apply
  // ----------------------------
  const handleApplyResize = () => {
    const newW = Number(pendingWidth);
    const newH = Number(pendingHeight);

    if (Number.isNaN(newW) || Number.isNaN(newH)) return setError('Width and Height must be valid numbers');
    if (newW < 5 || newH < 5) return setError('Map must be at least 5x5 squares (25ft x 25ft)');
    if (newW > 100 || newH > 100) return setError('Map cannot exceed 100x100 squares (500ft x 500ft)');

    const isShrinking = newW < mapWidth || newH < mapHeight;
    if (isShrinking) {
      const ok = window.confirm(
        `You are reducing the map size from ${mapWidth}×${mapHeight} to ${newW}×${newH}.\n\n` +
          `Tiles outside the new bounds will be permanently removed.\n\nContinue?`
      );
      if (!ok) {
        setPendingWidth(mapWidth);
        setPendingHeight(mapHeight);
        return;
      }
    }

    setMapWidth(newW);
    setMapHeight(newH);
    setGridData((prev) => resizeGrid(prev, newW, newH));
    setError(null);
  };

  const isResizeDirty = pendingWidth !== mapWidth || pendingHeight !== mapHeight;

  // ----------------------------
  // Painting + panning
  // ----------------------------
  const handleCellClick = (rowIndex, colIndex) => {
    const newGrid = [...gridData];
    newGrid[rowIndex] = [...newGrid[rowIndex]];
    newGrid[rowIndex][colIndex] = selectedTile;
    setGridData(newGrid);
  };

  const handleCellHover = (rowIndex, colIndex) => {
    if (isPainting) handleCellClick(rowIndex, colIndex);
  };

  const handleMouseDown = (e) => {
    if (e.button === 2) {
      e.preventDefault();
      setIsPainting(false);

      const el = canvasRef.current;
      if (!el) return;

      isPanningRef.current = true;
      setIsPanning(true);

      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: el.scrollLeft,
        scrollTop: el.scrollTop
      };
      return;
    }

    if (e.button === 0) setIsPainting(true);
  };

  const handleMouseUp = (e) => {
    if (!e || e.button === 0) setIsPainting(false);
  };

  const handleClearGrid = () => {
    if (window.confirm('Clear entire map? This cannot be undone.')) {
      initializeGrid(mapWidth, mapHeight);
    }
  };

  const handleFillGrid = () => {
    const name = allTileTypes[selectedTile]?.name || selectedTile;
    if (window.confirm(`Fill entire map with ${name}?`)) {
      setGridData((prev) => prev.map((row) => row.map(() => selectedTile)));
    }
  };

  // ----------------------------
  // Upload
  // ----------------------------
  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const tileKey = `custom_${Date.now()}`;
      const newTile = {
        key: tileKey,
        name: file.name.replace(/\.[^/.]+$/, ''),
        image: event.target.result,
        category: 'custom'
      };
      setCustomTiles((prev) => [...prev, newTile]);
      setTileCategory((prev) => ({ ...prev, [tileKey]: CAT.CUSTOM_ROOT }));
      setExpanded((prev) => ({ ...prev, [CAT.CUSTOM_ROOT]: true }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const tileKey = `custom_${Date.now()}`;
      const newTile = {
        key: tileKey,
        name: file.name.replace(/\.[^/.]+$/, ''),
        image: event.target.result,
        category: 'custom'
      };
      setCustomTiles((prev) => [...prev, newTile]);
      setTileCategory((prev) => ({ ...prev, [tileKey]: CAT.CUSTOM_ROOT }));
      setExpanded((prev) => ({ ...prev, [CAT.CUSTOM_ROOT]: true }));
    };
    reader.readAsDataURL(file);
  };

  const handleDragOverUpload = (e) => {
    e.preventDefault();
    setIsDraggingFile(true);
  };

  const handleDragLeaveUpload = (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
  };

  // Render tile content
  const renderTileContent = (tileKey) => {
    const tile = allTileTypes[tileKey];
    if (!tile || !tile.image) return null;
    return <img src={tile.image} alt={tile.name} className="tile-image" draggable={false} />;
  };

  const renderTileButton = (tileKey, tile) => (
    <button
      key={tileKey}
      className={`tile-button ${selectedTile === tileKey ? 'selected' : ''}`}
      onClick={() => setSelectedTile(tileKey)}
      title={tile.name}
      type="button"
      draggable
      onDragStart={(e) => startTileDrag(e, tileKey)}
    >
      {tile.image ? (
        <img src={tile.image} alt={tile.name} className="tile-button-preview" draggable={false} />
      ) : (
        <span className="tile-name">{tile.name}</span>
      )}
    </button>
  );

  const TreeNode = ({ cat, level }) => {
    const kids = getChildren(cat.id);
    const isOpen = !!expanded[cat.id];
    const tiles = tilesInCategory.get(cat.id) || [];

    return (
      <div style={{ marginTop: level === 0 ? 10 : 8 }}>
        <div onDragOver={onCategoryDragOver} onDrop={(e) => onCategoryDrop(e, cat.id)} style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => toggleExpanded(cat.id)}
            aria-expanded={isOpen}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 10px',
              textAlign: 'left'
            }}
            title="Drop tiles here to assign to this category"
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
              <span style={{ paddingLeft: level * 10 }}>{cat.name}</span>
            </span>
            <span style={{ opacity: 0.8, fontSize: 12 }}>
              {tiles.length ? `${tiles.length} tile${tiles.length === 1 ? '' : 's'}` : isOpen ? 'Hide' : 'Show'}
            </span>
          </button>
        </div>

        {isOpen && (
          <div style={{ marginLeft: level === 0 ? 0 : 12, marginTop: 10 }}>
            {tiles.length > 0 && (
              <div className="tile-grid" style={{ marginBottom: kids.length ? 10 : 0 }}>
                {tiles.map(({ tileKey, tile }) => renderTileButton(tileKey, tile))}
              </div>
            )}
            {kids.map((child) => (
              <TreeNode key={child.id} cat={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // ----------------------------
  // LIST VIEW
  // ----------------------------
  if (!isCreating && !isEditing) {
    return (
      <div className="maps-section">
        <div className="section-header">
          <h2>Campaign Maps</h2>
          <button className="btn-primary" onClick={handleCreateNew}>
            Create New Map
          </button>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="loading-message">
            <div className="spinner"></div>
            <p>Loading maps...</p>
          </div>
        ) : maps.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🗺️</div>
            <h3>No Maps Yet</h3>
            <p>Create your first encounter map to get started</p>
          </div>
        ) : (
          <div className="maps-grid">
            {maps.map((map) => (
              <div key={map.id} className="map-card">
                <div className="map-preview">
                  <div className="map-preview-grid">
                    {JSON.parse(map.gridData)
                      .slice(0, 10)
                      .map((row, y) => (
                        <div key={y} className="preview-row">
                          {row.slice(0, 10).map((tileKey, x) => {
                            const t = TILE_TYPES[tileKey];
                            return <div key={x} className="preview-cell">{t?.image ? <img src={t.image} alt={t.name} className="preview-cell-image" draggable={false} /> : null}</div>;
                          })}
                        </div>
                      ))}
                  </div>
                </div>
                <div className="map-info">
                  <h3>{map.name}</h3>
                  <p className="map-dimensions">
                    {map.width}x{map.height} squares ({map.width * 5}ft x {map.height * 5}ft)
                  </p>
                  <div className="map-actions">
                    <button className="btn-secondary" onClick={() => handleEditMap(map)}>
                      Edit Map
                    </button>
                    <button className="btn-danger" onClick={() => handleDeleteMap(map.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ----------------------------
  // EDITOR VIEW
  // ----------------------------
  const rootCategories = getChildren(null);

  const parentOptions = categories
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({ id: c.id, label: c.parentId ? `↳ ${c.name}` : c.name }));

  return (
    <div className="map-editor">
      <div className="section-header">
        <h2>{isEditing ? 'Edit Map' : 'Create New Map'}</h2>
        <div className="section-actions">
          <button className="btn-secondary" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSaveMap} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Map'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="map-editor-content">
        {/* Left Panel */}
        <div className="editor-panel settings-panel">
          <div className="panel-section">
            <h3>Map Settings</h3>

            <div className="form-group">
              <label>Map Name</label>
              <input
                type="text"
                value={mapName}
                onChange={(e) => setMapName(e.target.value)}
                placeholder="e.g., Abandoned Warehouse"
                disabled={isSaving}
              />
            </div>

            <div className="form-group">
              <label>Width (5ft squares)</label>
              <input
                type="number"
                min={5}
                max={100}
                value={pendingWidth}
                onChange={(e) => setPendingWidth(Number(e.target.value))}
                disabled={isSaving}
              />
              <span className="hint">{mapWidth * 5} feet</span>
            </div>

            <div className="form-group">
              <label>Height (5ft squares)</label>
              <input
                type="number"
                min={5}
                max={100}
                value={pendingHeight}
                onChange={(e) => setPendingHeight(Number(e.target.value))}
                disabled={isSaving}
              />
              <span className="hint">{mapHeight * 5} feet</span>
            </div>

            <div className="form-group" style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn-primary"
                type="button"
                onClick={handleApplyResize}
                disabled={isSaving || !isResizeDirty}
                style={{ flex: 1 }}
              >
                Apply Resize
              </button>
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setPendingWidth(mapWidth);
                  setPendingHeight(mapHeight);
                }}
                disabled={isSaving || !isResizeDirty}
                style={{ flex: 1 }}
              >
                Revert
              </button>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} />
                Show Grid Lines
              </label>
            </div>
          </div>

          <div className="panel-section">
            <h3>Tile Upload</h3>

            <div
              className={`tile-upload-zone ${isDraggingFile ? 'dragging' : ''}`}
              onClick={() => {
                if (!isSaving) fileInputRef.current?.click();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isSaving) fileInputRef.current?.click();
              }}
              onDrop={handleFileDrop}
              onDragOver={handleDragOverUpload}
              onDragLeave={handleDragLeaveUpload}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                disabled={isSaving}
              />
              <span className="upload-icon">📁</span>
              <p>Click to upload or drag & drop tile images here</p>
              <span className="hint">PNG, JPG (recommended: 30x30px)</span>
            </div>
          </div>

          <div className="panel-section">
            <h3>Tile Categories</h3>

            <div className="form-group">
              <label>Create Category</label>
              <input
                type="text"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder="e.g., Dungeon / Sci-Fi / City"
                disabled={isSaving}
              />
              <span className="hint">Choose where it should live (root or as a subcategory).</span>
            </div>

            <div className="form-group">
              <label>Parent</label>
              <select
                value={newCatParent ?? ''}
                onChange={(e) => setNewCatParent(e.target.value || null)}
                disabled={isSaving}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(15, 5, 29, 0.6)',
                  border: '1px solid #5a189a',
                  borderRadius: 5,
                  color: '#e0d8f0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.95rem'
                }}
              >
                {parentOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <button className="btn-primary full-width" type="button" onClick={addCategory} disabled={isSaving}>
              Add Category
            </button>

            <div style={{ marginTop: 14 }}>
              {rootCategories.map((cat) => (
                <TreeNode key={cat.id} cat={cat} level={0} />
              ))}
            </div>

            <p className="hint" style={{ marginTop: 12 }}>
              Tip: drag any tile button and drop it on a category header to move it.
            </p>
          </div>

          <div className="panel-section">
            <h3>Tools</h3>
            <button className="btn-secondary full-width" onClick={handleClearGrid}>
              Clear Map
            </button>
            <button className="btn-secondary full-width" onClick={handleFillGrid}>
              Fill with Selected Tile
            </button>
          </div>
        </div>

        {/* Right Panel: Canvas */}
        <div className="editor-panel canvas-panel">
          <div className="canvas-header">
            <h3>
              {mapName || 'Untitled Map'}{' '}
              <span className="canvas-dimensions">
                ({mapWidth}x{mapHeight} squares)
              </span>
            </h3>

            <div className="canvas-controls" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="selected-tile-indicator">
                Selected: <strong>{allTileTypes[selectedTile]?.name || 'Unknown'}</strong>
                <span className="tile-preview">
                  {allTileTypes[selectedTile]?.image ? (
                    <img
                      src={allTileTypes[selectedTile].image}
                      alt={allTileTypes[selectedTile].name}
                      className="preview-tile-image"
                      draggable={false}
                    />
                  ) : null}
                </span>
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" className="btn-secondary" onClick={zoomOut} title="Zoom out">
                  −
                </button>
                <div style={{ minWidth: 56, textAlign: 'center', color: '#e0d8f0', fontSize: 14 }}>
                  {Math.round(zoom * 100)}%
                </div>
                <button type="button" className="btn-secondary" onClick={zoomIn} title="Zoom in">
                  +
                </button>
                <button type="button" className="btn-secondary" onClick={zoomReset} title="Reset zoom to 100%">
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className={`canvas-container ${isPanning ? 'is-panning' : ''}`} ref={canvasRef} onContextMenu={(e) => e.preventDefault()}>
            <div className="canvas-center">
                <div
                  ref={gridRef}
                  className={`map-grid ${showGrid ? 'show-grid' : ''}`}
                  style={{
                    gridTemplateColumns: `repeat(${mapWidth}, var(--map-cell-size))`,
                    gridTemplateRows: `repeat(${mapHeight}, var(--map-cell-size))`,
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={() => setIsPainting(false)}
                  title="Tip: Ctrl+Mousewheel (or Cmd+Mousewheel on Mac) to zoom"
                >
                {gridData.map((row, rowIndex) => (
                  <div key={rowIndex} className="grid-row">
                    {row.map((tileKey, colIndex) => (
                      <div
                        key={colIndex}
                        className="grid-cell"
                        onClick={() => handleCellClick(rowIndex, colIndex)}
                        onMouseEnter={() => handleCellHover(rowIndex, colIndex)}
                        title={`(${colIndex}, ${rowIndex}) - ${allTileTypes[tileKey]?.name || 'Unknown'}`}
                      >
                        {renderTileContent(tileKey)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="canvas-footer">
            <p className="hint">
              Click cells to place tiles, or click and drag to paint. Right-click and drag to pan. Ctrl/Cmd + wheel to zoom.
              Current map size: {mapWidth * 5}ft × {mapHeight * 5}ft
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Maps;
